import React, { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
    Typography, Alert, Box, Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AsaService } from 'crqlar-react-common/dist/services/integrations/asa/asa.service';
import { LoadingButton } from '@mui/lab';
import { AsaCredentials } from 'crqlar-react-common/dist/models/integrations/asa/asa.config';
import CDatePicker from 'crqlar-react-common/dist/components/CDatePicker';
import { formatDateLocalISO } from 'crqlar-react-common/dist/utils/date.util';
import { useServicesForSector } from 'crqlar-react-common/dist/utils/hooks/services/useServices';
import { Service } from 'crqlar-react-common/dist/models/service/service.model';

interface Props {
    open: boolean;
    onClose: () => void;
    asaCredentials: AsaCredentials;
    spaSectorId?: string;
}

const CheckAvailabilityDialog = ({ open, onClose, asaCredentials, spaSectorId }: Props) => {
    const { t } = useTranslation();
    const [selectedService, setSelectedService] = useState<Service | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [result, setResult] = useState<any>(null);
    
    const services = useServicesForSector(spaSectorId);
    const asaServices = services.filter(service => 
        service.managedExternally && 
        service.externalInfo?.some(info => info.provider === 'asa')
    );

    const checkAvailabilityMutation = useMutation(
        'checkAvailability',
        async () => {
            if (!selectedService?.marketCode) {
                throw new Error('No market code available for selected service');
            }
            const isoDate = formatDateLocalISO(selectedDate) || formatDateLocalISO(new Date()) || '';
            return await AsaService.checkAvailability(asaCredentials, selectedService.marketCode, isoDate);
        },
        {
            onSuccess: (data) => {
                setResult(data);
            }
        }
    );

    const handleSubmit = () => {
        setResult(null);
        checkAvailabilityMutation.mutate();
    };

    const handleClose = () => {
        setSelectedService(null);
        setSelectedDate(new Date());
        setResult(null);
        checkAvailabilityMutation.reset();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>{t('Check Service Availability')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        {t('Select a service to check its availability in the ASA system.')}
                    </Alert>

                    <Autocomplete
                        value={selectedService}
                        onChange={(event, newValue) => setSelectedService(newValue)}
                        options={asaServices}
                        getOptionLabel={(option) => option.name || ''}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('Service')}
                                placeholder={t('Select service')}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        fullWidth
                    />

                    <CDatePicker
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date || new Date())}
                        label={t('Date')}
                        dateOnly
                        min={new Date()}
                    />

                    {checkAvailabilityMutation.isError && (
                        <Typography color="error">
                            {t('An error occurred while checking availability')}
                        </Typography>
                    )}

                    {result && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {t('Result')}:
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    backgroundColor: '#f5f5f5',
                                    padding: 2,
                                    borderRadius: 1,
                                    overflow: 'auto',
                                    maxHeight: 400,
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {JSON.stringify(result, null, 2)}
                            </Box>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {t('Close')}
                </Button>
                <LoadingButton
                    loading={checkAvailabilityMutation.isLoading}
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedService}
                >
                    {t('Check Availability')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CheckAvailabilityDialog;